import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, Truck, Package, Zap, Layers, Shield, ArrowUpDown, Box, Globe, Clock, MapPin } from 'lucide-react';
import { ImageUpload } from '../ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

const iconMap = { Truck, Package, Zap, Layers, Shield, ArrowUpDown, Box, Globe, Clock, MapPin };
const iconOptions = Object.keys(iconMap);

export default function ServicesManager() {
  const [services, setServices] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'OUR SERVICES', hero_subtitle_bg: 'НАШИТЕ УСЛУГИ', hero_subtitle_de: 'UNSERE DIENSTLEISTUNGEN',
    hero_title_en: 'Comprehensive', hero_title_bg: 'Цялостни', hero_title_de: 'Umfassende',
    hero_title_highlight_en: 'Transport Solutions', hero_title_highlight_bg: 'транспортни решения', hero_title_highlight_de: 'Transportlösungen',
    hero_description_en: 'From express deliveries to full relocations, we offer a complete range of transport services.',
    hero_description_bg: 'От експресни доставки до пълни премествания, предлагаме пълна гама от транспортни услуги.',
    hero_description_de: 'Von Expresslieferungen bis hin zu kompletten Umzügen bieten wir umfassende Transportdienstleistungen.',
    cta_title_en: 'Need a Custom Solution?', cta_title_bg: 'Нуждаете се от персонализирано решение?', cta_title_de: 'Brauchen Sie eine individuelle Lösung?',
    cta_text_en: 'Contact us to discuss your specific requirements.', cta_text_bg: 'Свържете се с нас за вашите специфични изисквания.', cta_text_de: 'Kontaktieren Sie uns für Ihre spezifischen Anforderungen.',
    cta_button_en: 'Contact Us', cta_button_bg: 'Свържете се с нас', cta_button_de: 'Kontaktieren Sie uns'
  });
  
  const defaultForm = {
    title_en: '', title_bg: '', title_de: '',
    short_description_en: '', short_description_bg: '', short_description_de: '',
    full_description_en: '', full_description_bg: '', full_description_de: '',
    features_en: '', features_bg: '', features_de: '',
    icon: 'Truck', image_url: '', order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchServices(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/services-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/services-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/admin/services`);
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        features_en: formData.features_en ? formData.features_en.split('\n').filter(f => f.trim()) : [],
        features_bg: formData.features_bg ? formData.features_bg.split('\n').filter(f => f.trim()) : [],
        features_de: formData.features_de ? formData.features_de.split('\n').filter(f => f.trim()) : []
      };
      
      if (editingService) {
        await axios.put(`${API}/admin/services/${editingService.id}`, submitData);
        toast.success('Service updated!');
      } else {
        await axios.post(`${API}/admin/services`, submitData);
        toast.success('Service added!');
      }
      fetchServices();
      setIsFormOpen(false);
      setEditingService(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`);
      toast.success('Service deleted!');
      fetchServices();
    } catch (error) {
      toast.error('Error deleting service');
    }
  };

  const startEdit = (service) => {
    setEditingService(service);
    setFormData({
      title_en: service.title_en || service.title || '',
      title_bg: service.title_bg || '',
      title_de: service.title_de || '',
      short_description_en: service.short_description_en || service.short_description || '',
      short_description_bg: service.short_description_bg || '',
      short_description_de: service.short_description_de || '',
      full_description_en: service.full_description_en || '',
      full_description_bg: service.full_description_bg || '',
      full_description_de: service.full_description_de || '',
      features_en: Array.isArray(service.features_en) ? service.features_en.join('\n') : '',
      features_bg: Array.isArray(service.features_bg) ? service.features_bg.join('\n') : '',
      features_de: Array.isArray(service.features_de) ? service.features_de.join('\n') : '',
      icon: service.icon || 'Truck',
      image_url: service.image_url || '',
      order: service.order || 0,
      is_active: service.is_active !== false
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updatePageField = (field, value) => {
    setPageContent(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Services Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      <div className="grid gap-3">
                        <div>
                          <Label className="text-xs">Subtitle</Label>
                          <Input 
                            value={pageContent[`hero_subtitle_${lang.code}`] || ''} 
                            onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} 
                            className="mt-1 bg-white" 
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Title</Label>
                            <Input 
                              value={pageContent[`hero_title_${lang.code}`] || ''} 
                              onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} 
                              className="mt-1 bg-white" 
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Title Highlight (colored)</Label>
                            <Input 
                              value={pageContent[`hero_title_highlight_${lang.code}`] || ''} 
                              onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} 
                              className="mt-1 bg-white" 
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Textarea 
                            value={pageContent[`hero_description_${lang.code}`] || ''} 
                            onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} 
                            className="mt-1 bg-white" 
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">CTA Title</Label>
                            <Input 
                              value={pageContent[`cta_title_${lang.code}`] || ''} 
                              onChange={(e) => updatePageField(`cta_title_${lang.code}`, e.target.value)} 
                              className="mt-1 bg-white" 
                            />
                          </div>
                          <div>
                            <Label className="text-xs">CTA Button</Label>
                            <Input 
                              value={pageContent[`cta_button_${lang.code}`] || ''} 
                              onChange={(e) => updatePageField(`cta_button_${lang.code}`, e.target.value)} 
                              className="mt-1 bg-white" 
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Services</h2>
          <p className="text-sm text-slate-500">Manage your transport services</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingService(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{editingService ? 'Edit Service' : 'New Service'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setIsFormOpen(false); setEditingService(null); }}><X className="w-4 h-4" /></Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Language Tabs for Content */}
              <Tabs value={activeLang} onValueChange={setActiveLang} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                      {lang.flag} {lang.name}
                      {formData[`title_${lang.code}`] && <span className="ml-1 text-green-500">✓</span>}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      
                      <div>
                        <Label>Service Title {lang.code === 'en' && '*'}</Label>
                        <Input 
                          value={formData[`title_${lang.code}`]} 
                          onChange={(e) => updateField(`title_${lang.code}`, e.target.value)} 
                          className="mt-1 rounded-xl bg-white" 
                          required={lang.code === 'en'}
                          placeholder={lang.code === 'en' ? 'e.g., International Moving' : lang.code === 'bg' ? 'напр., Международно преместване' : 'z.B., Internationale Umzüge'}
                        />
                      </div>
                      
                      <div>
                        <Label>Short Description {lang.code === 'en' && '*'}</Label>
                        <Textarea 
                          value={formData[`short_description_${lang.code}`]} 
                          onChange={(e) => updateField(`short_description_${lang.code}`, e.target.value)} 
                          className="mt-1 rounded-xl bg-white" 
                          required={lang.code === 'en'}
                          rows={2}
                        />
                      </div>
                      
                      <div>
                        <Label>Full Description</Label>
                        <Textarea 
                          value={formData[`full_description_${lang.code}`]} 
                          onChange={(e) => updateField(`full_description_${lang.code}`, e.target.value)} 
                          className="mt-1 rounded-xl bg-white" 
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label>Features (one per line)</Label>
                        <Textarea 
                          value={formData[`features_${lang.code}`]} 
                          onChange={(e) => updateField(`features_${lang.code}`, e.target.value)} 
                          className="mt-1 rounded-xl bg-white" 
                          rows={4}
                          placeholder={lang.code === 'en' ? 'Feature 1\nFeature 2\nFeature 3' : ''}
                        />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {/* Icon, Image, Order */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Icon</Label>
                  <Select value={formData.icon} onValueChange={(value) => updateField('icon', value)}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Select icon" />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Image</Label>
                  <div className="mt-1">
                    <ImageUpload 
                      value={formData.image_url} 
                      onChange={(url) => updateField('image_url', url)} 
                      placeholder="Enter URL or upload image"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Order</Label>
                    <Input type="number" value={formData.order} onChange={(e) => updateField('order', parseInt(e.target.value) || 0)} className="mt-1 rounded-xl" />
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.is_active} onChange={(e) => updateField('is_active', e.target.checked)} className="rounded" />
                      <span className="text-sm">Active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsFormOpen(false); setEditingService(null); }} className="rounded-full">Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full"><Save className="w-4 h-4 mr-2" /> Save Service</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Services List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service) => {
          const IconComponent = iconMap[service.icon] || Truck;
          return (
            <Card key={service.id} className={`bg-white rounded-2xl border-0 shadow-sm ${!service.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-[#C41E3A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-slate-900 truncate">{service.title_en || service.title}</h4>
                      {!service.is_active && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Hidden</span>}
                    </div>
                    {/* Language badges */}
                    <div className="flex gap-1 mb-2">
                      {LANGUAGES.map(lang => (
                        <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${service[`title_${lang.code}`] ? lang.bg + ' ' + lang.text : 'bg-slate-100 text-slate-400'}`}>
                          {lang.flag}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{service.short_description_en || service.short_description}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => startEdit(service)} className="rounded-full"><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(service.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {services.length === 0 && <p className="text-slate-500 text-center py-8 col-span-2">No services yet. Add your first service!</p>}
      </div>
    </div>
  );
}
